import Util from './lib/util.js'
import Masonry from 'masonry-layout'

const BM_Theme = (
	function () {
		'use strict'

		const publicAPIs = {}

		publicAPIs.init = function ( options ) {
			// functions that need to wait for the DOM to load
			if ( document.readyState === "interactive" || document.readyState === "complete" ) {
				constructorEarly( options );
			} else {
				document.addEventListener( "DOMContentLoaded", constructorEarly, options );
			}

			// functions that need to wait for the DOM and images/stylesheets to load
			if ( document.readyState === "complete" ) {
				constructorLate( options );
			} else {
				window.addEventListener( "load", constructorLate, options );
			}
		}

		const constructorEarly = function () {
			// Header navigation
			initNavigation()

			// Improve clickable regions on cards without damaging accessibility
			initClickableCards()

			// Add wrapper div to youtube and vimeo iframes to make them responsive
			resizeIframeVideos();

			// Click-to-toggle elements (https://codyhouse.co/ds/components/info/collapse)
			initCollapse();
		}

		const constructorLate = function () {
			// adds a CSS class to the primary nav when its items are line-wrapped
			initDetectWhenNavItemsLineWrap();

			// Featured image grid (via David DeSandro's Masonry)
			initFeaturedImageGrid();
		}

		const initDetectWhenNavItemsLineWrap = function () {
			const menu = document.getElementById( "primary-menu" );
			if ( !menu ) return;

			function onLineWrapDoSomething() {
				menu.classList.remove( "is-line-wrapped" );

				for ( let i = 0; i < menu.children.length; i ++ ) {
					if ( !menu.children[i].getElementsByTagName( 'a' ) ) continue;

					let menu_item_link = menu.children[i].getElementsByTagName( 'a' )[0];

					// make sure link contains text that is at least two words
					if ( !menu_item_link.textContent || !menu_item_link.textContent.includes( ' ' ) ) continue;

					let widthBefore = menu_item_link.offsetWidth;
					menu_item_link.style.whiteSpace = "nowrap";

					let widthAfter = menu_item_link.offsetWidth;
					menu_item_link.style.whiteSpace = "";

					if ( widthBefore !== widthAfter ) {
						menu.classList.add( "is-line-wrapped" );
					}

					return;
				}
			}

			let resizeTimeout;
			window.addEventListener( 'resize', function () {
				clearTimeout( resizeTimeout );
				resizeTimeout = setTimeout( onLineWrapDoSomething, 50 );
			} )
		}

		const initCollapse = function () {
			// File#: _1_collapse
			// Usage: codyhouse.co/license
			(function () {
				let Collapse = function ( element ) {
					this.element = element;
					this.triggers = document.querySelectorAll( '[aria-controls="' + this.element.getAttribute( 'id' ) + '"]' );
					this.animate = this.element.getAttribute( 'data-collapse-animate' ) === 'on';
					this.animating = false;
					initCollapse( this );
				};

				function initCollapse( element ) {
					if ( element.triggers ) {
						// set initial 'aria-expanded' attribute for trigger elements
						updateTriggers( element, !Util.hasClass( element.element, 'is-hidden' ) );

						// detect click on trigger elements
						for ( let i = 0; i < element.triggers.length; i ++ ) {
							element.triggers[i].addEventListener( 'click', function ( event ) {
								event.preventDefault();
								toggleVisibility( element );
							} );
						}
					}

					// custom event
					element.element.addEventListener( 'collapseToggle', function ( event ) {
						toggleVisibility( element );
					} );
				}

				function toggleVisibility( element ) {
					let bool = Util.hasClass( element.element, 'is-hidden' );
					if ( element.animating ) return;
					element.animating = true;
					animateElement( element, bool );
					updateTriggers( element, bool );
				}

				function animateElement( element, bool ) {
					// bool === true -> show content
					if ( !element.animate || !window.requestAnimationFrame ) {
						Util.toggleClass( element.element, 'is-hidden', !bool );
						element.animating = false;
						return;
					}

					// animate content height
					Util.removeClass( element.element, 'is-hidden' );
					let initHeight = !bool ? element.element.offsetHeight : 0,
						finalHeight = !bool ? 0 : element.element.offsetHeight;

					Util.addClass( element.element, 'overflow-hidden' );

					Util.setHeight( initHeight, finalHeight, element.element, 200, function () {
						if ( !bool ) Util.addClass( element.element, 'is-hidden' );
						element.element.removeAttribute( "style" );
						Util.removeClass( element.element, 'overflow-hidden' );
						element.animating = false;
					}, 'easeInOutQuad' );
				}

				function updateTriggers( element, bool ) {
					for ( let i = 0; i < element.triggers.length; i ++ ) {
						bool ? element.triggers[i].setAttribute( 'aria-expanded', 'true' ) : element.triggers[i].removeAttribute( 'aria-expanded' );
					}
				}

				window.Collapse = Collapse;

				//initialize the Collapse objects
				let collapses = document.getElementsByClassName( 'js-collapse' );
				if ( collapses.length > 0 ) {
					for ( let i = 0; i < collapses.length; i ++ ) {
						new Collapse( collapses[i] );
					}
				}
			}());
		}

		const initFeaturedImageGrid = function () {
			let grid = document.getElementById( 'featured-items' );
			if ( !grid ) return;

			// sometimes rounding differences leave weird gaps. see https://codepen.io/rosieleung/pen/bGKMZoy
			Masonry.prototype._getTopColPosition = function ( colSpan ) {
				let colGroup = this._getTopColGroup( colSpan );
				let minimumY = Math.min.apply( Math, colGroup );
				let col = colGroup.indexOf( minimumY );
				let maxColumnHeightDifference = this._getOption( 'maxColumnHeightDifference' );
				if ( maxColumnHeightDifference && maxColumnHeightDifference > 0 ) {
					for ( let c = 0, n = colGroup.length; c < n; c ++ ) {
						if ( colGroup[c] <= minimumY + maxColumnHeightDifference ) {
							col = c;
							break;
						}
					}
				}

				return {
					col: col,
					y: minimumY,
				};
			};

			let msnry = new Masonry( grid, {
				itemSelector: '.featured-item',
				gutter: '.gutter-sizer',
				columnWidth: '.grid-sizer',
				percentPosition: true,
				maxColumnHeightDifference: 2
			} );
		}

		const initNavigation = function () {
			const $primaryMenu = document.getElementById( 'primary-menu' )
			if ( !$primaryMenu ) {
				return false
			}

			const $dropdownItems = $primaryMenu.querySelectorAll( ":scope .menu-item-has-children" );
			initClickableMobileDropdowns();

			function initClickableMobileDropdowns() {
				for ( const item of $dropdownItems ) {
					item.setAttribute( 'aria-expanded', "false" );

					let list = item.querySelector( ":scope > ul" );
					list.setAttribute( "data-collapse-animate", "on" );

					// hijack click to toggle the dropdown
					let link = item.querySelector( ":scope > a" );
					link.addEventListener( "click", function ( e ) {
						// only hijack when in mobile
						if ( window.getComputedStyle( $primaryMenu ).display === "flex" ) return;

						// get the parent item
						e.preventDefault();
						let self = e.target;
						while ( !self.classList.contains( 'menu-item-has-children' ) ) {
							self = self.parentNode
						}

						// toggle the aria attribute
						if ( self.getAttribute( "aria-expanded" ) === "true" ) {
							self.setAttribute( 'aria-expanded', "false" );
						} else {
							self.setAttribute( 'aria-expanded', "true" );
						}

						// toggle the submenu
						let submenu = self.getElementsByClassName( 'sub-menu' )[0];
						let event = new CustomEvent( 'collapseToggle' );
						submenu.dispatchEvent( event );
					}, true );
				}
			}
		}

		const initClickableCards = function () {
			// in .card blocks, clicking on .card-fake-link triggers a click on .card-link
			// also, hovering over .card-link and .card-fake-link adds the .card-hover class to .card
			const cards = document.querySelectorAll( '.card' )
			Array.prototype.forEach.call( cards, card => {
				if ( card.getAttribute( "data-clickable" ) ) return;
				card.setAttribute( "data-clickable", true );
				let down,
					up,
					fakeLinks,
					cardHoverClass = 'card-hover',
					link = card.classList.contains( "card-link" ) ? card : card.querySelector( '.card-link' );

				if ( card.classList.contains( "card-fake-link" ) ) {
					fakeLinks = card;
				} else {
					fakeLinks = card.querySelectorAll( '.card-fake-link' )
				}

				if ( link ) {
					link.onmouseover = () => {
						card.classList.add( cardHoverClass )
					}
					link.onmouseout = () => {
						card.classList.remove( cardHoverClass )
					}
					if ( fakeLinks ) {
						if ( card.classList.contains( "card-fake-link" ) ) {
							card.style.cursor = 'pointer'
							card.onmouseover = () => {
								card.classList.add( cardHoverClass )
							}
							card.onmouseout = () => {
								card.classList.remove( cardHoverClass )
							}
							card.onmousedown = ( e ) => {
								if ( e.button === 0 ) {
									down = + new Date()
								}
							}
							card.onmouseup = () => {
								up = + new Date()
								if ( (up - down) < 200 ) {
									link.click();
								}
							}
						} else {
							Array.prototype.forEach.call( Array.from( fakeLinks ), fakeLink => {
								fakeLink.style.cursor = 'pointer'
								fakeLink.onmouseover = () => {
									card.classList.add( cardHoverClass )
								}
								fakeLink.onmouseout = () => {
									card.classList.remove( cardHoverClass )
								}
								fakeLink.onmousedown = ( e ) => {
									if ( e.button === 0 ) {
										down = + new Date()
									}
								}
								fakeLink.onmouseup = () => {
									up = + new Date()
									if ( (up - down) < 200 ) {
										link.click();
									}
								}
							} )
						}
					}
				}
			} )
		}

		const resizeIframeVideos = function () {
			// props to https://css-tricks.com/fluid-width-video/

			function wrap( el, wrapper ) {
				el.parentNode.insertBefore( wrapper, el );
				wrapper.appendChild( el );
			}

			let iframes = document.querySelectorAll( 'iframe[src*="youtube.com"], iframe[src*="vimeo.com"], iframe[src*="google.com/maps"]' );
			if ( !iframes.length ) {
				return;
			}

			for ( let i = 0; i < iframes.length; i ++ ) {
				let iframe = iframes[i];

				// save and remove hardcoded height and width
				let width = iframe.getAttribute( "width" );
				let height = iframe.getAttribute( "height" );
				iframe.removeAttribute( "height" );
				iframe.removeAttribute( "width" );

				// wrap in div with correct aspect ratio
				let div = document.createElement( "div" );
				div.className = "fitvid";
				div.style.paddingBottom = height / width * 100 + "%";
				wrap( iframe, div );
			}
		}
		return publicAPIs
	}
)
()

BM_Theme.init()
